import { Check, History } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Throbber } from "./ui/throbber";

interface RevisionLike {
  id: string;
  createdAt: string;
  source: string;
}

interface RevisionHistorySelectProps {
  sortedRevisions: RevisionLike[];
  currentRevisionId: string | null | undefined;
  /** The revision currently being viewed, for the "Aktuell — <date>" label. */
  viewingRevision?: RevisionLike | null;
  isSelecting: boolean;
  selectRevision: (revisionId: string) => void;
  getSourceLabel: (source: string) => string;
}

const formatRevisionTime = (iso: string): string =>
  new Date(iso).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Borderless version-history control shared by Venus and Neptune. Lists the
 * current revision plus every older one; selecting one moves the pointer via
 * `selectRevision`. Renders nothing when there is no older revision to switch
 * to. Driven entirely by the values `useProposedAnswer` already returns, so both
 * apps render the identical control on the answer's header line.
 */
export function RevisionHistorySelect({
  sortedRevisions,
  currentRevisionId,
  isSelecting,
  selectRevision,
  getSourceLabel,
}: RevisionHistorySelectProps) {
  // Only worth showing once there's more than one revision to move between.
  if (sortedRevisions.length < 2) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        title="Version"
        className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
      >
        <History className="h-4 w-4" />
        {isSelecting && <Throbber className="h-4 w-4" />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {/* Every revision is listed; the current one carries the check + highlight
            instead of a separate "Aktuell" entry (which read as an obsolete
            duplicate of the marked row). */}
        {sortedRevisions.map((rev) => {
          const isCurrent = rev.id === currentRevisionId;
          return (
            <DropdownMenuItem
              key={rev.id}
              onSelect={() => selectRevision(rev.id)}
              className={isCurrent ? "font-medium bg-accent/60 focus:bg-accent" : "pl-7"}
            >
              {isCurrent && <Check className="h-3.5 w-3.5 shrink-0" />}
              {getSourceLabel(rev.source)} — {formatRevisionTime(rev.createdAt)}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
